import { ArrowLeftIcon, CalendarIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Badge, Callout, Card, Spinner } from '@radix-ui/themes';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE, Team } from '../api';
import ScoreSearchForm from './ScoreSearchForm';
import './ScoreResearcher.css';
import './ScoreView.css';

interface Game {
  id: number;
  date: string;
  winner: Team;
  loser: Team;
  higher_score: number;
  lower_score: number;
}

const gameDate = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

export default function ScoreView() {
  const { higherScore = '', lowerScore = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTeam = searchParams.get('team_id');
  const teamId = rawTeam && /^-?\d+$/.test(rawTeam) && Number.isSafeInteger(Number(rawTeam)) ? Number(rawTeam) : null;
  const validScore = [higherScore, lowerScore].every(score => /^\d+$/.test(score) && Number.isSafeInteger(Number(score)))
    && Number(higherScore) >= Number(lowerScore);
  const [games, setGames] = useState<Game[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!validScore) return;
    const controller = new AbortController();
    setGames(null);
    setFailed(false);
    const params = teamId === null ? undefined : { team_id: teamId };
    axios.get<{ games: Game[] }>(`${API_BASE}/api/games/${higherScore}/${lowerScore}/`, { params, signal: controller.signal })
      .then(response => setGames(response.data.games))
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [higherScore, lowerScore, teamId, validScore]);

  function changeTeam(id: number | null) {
    const params = new URLSearchParams(searchParams);
    if (id === null) params.delete('team_id');
    else params.set('team_id', String(id));
    setSearchParams(params);
  }

  const resultMessage = !validScore ? 'Enter two whole scores with the higher score first.'
    : failed ? 'Game history couldn’t load.'
    : games?.length === 0 ? 'No games in the archive match this search.' : '';

  return (
    <div className="results-page">
      <Link to="/score_lookup" className="back-link"><ArrowLeftIcon /> Back to score lookup</Link>
      <header className="results-header">
        <p className="eyebrow"><span className="status-dot" /> Final score archive</p>
        <h1 className="display-title result-title">{validScore ? <>{higherScore}<span>–</span>{lowerScore}</> : 'Invalid score'}</h1>
      </header>
      <div className="results-layout">
        <section className="results-list" aria-labelledby="games-title">
          <div className="results-list-heading">
            <h2 className="section-title" id="games-title">Game history</h2>
            {validScore && !failed && games && (
              <Badge color="yellow" variant="soft">{games.length.toLocaleString()} {games.length === 1 ? 'game' : 'games'}</Badge>
            )}
          </div>
          {resultMessage ? (
            <Callout.Root color={failed ? 'red' : 'gray'} role={failed ? 'alert' : 'status'}>
              <Callout.Icon><InfoCircledIcon /></Callout.Icon>
              <Callout.Text>{resultMessage}</Callout.Text>
            </Callout.Root>
          ) : games === null ? (
            <div className="games-loading" role="status"><Spinner /> Loading game history…</div>
          ) : (
            <ul className="game-list">
              {games.map(game => (
                <li key={game.id}>
                  <Card className="game-row">
                    <time dateTime={game.date}><CalendarIcon /> {gameDate.format(new Date(game.date))}</time>
                    <div className="game-teams"><strong>{game.winner.name}</strong><span>{game.loser.name}</span></div>
                    <span className="game-final">{game.higher_score}<span>–</span>{game.lower_score}</span>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
        <aside>
          <ScoreSearchForm key={`${higherScore}-${lowerScore}`} higher={higherScore} lower={lowerScore} teamId={teamId} onTeamChange={changeTeam} />
        </aside>
      </div>
    </div>
  );
}
